// MEMBRESÍAS F1 (20-oct-2026). Crear/editar un plan: nombre, precio (informativo), vigencia, si acumula puntos, beneficios
// (reglas que aplica el motor en F2) y aviso de vencimiento (cron F4: correo por defecto). Validación real en lib/memberships.js.
// Vigencia (Carlos 21-oct-2026: «dos sitios para poner meses confunde»): UNA pregunta «¿Cómo vence?» con dos modos.
// 'tiempo' = duration_months; 'productos' = quota_total + opcional leftover_months (lo que sobre caduca a los N meses;
// por defecto no caduca). Un plan viejo con tiempo Y productos se abre como «productos» con esa caducidad (mismo efecto:
// vence lo primero; al renovar cuenta desde hoy en vez de extender desde el fin).
import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { BadgeCheck, Loader2, X } from 'lucide-react';
import { membershipsApi, type BenefitRule, type MembershipPlan, type PlanInput } from '../../../api/membershipsApi';

interface Props {
  plan?: MembershipPlan | null;
  rewards: { id: string; title: string; isActive?: boolean }[];
  loyaltyMode: 'visits' | 'cashback';
  onClose: () => void;
  onSaved: (plan: MembershipPlan) => void;
}

const MESES = [1, 2, 3, 6, 12];
const cls = 'w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-content outline-none focus:border-accent/50';
const lbl = 'mb-1 block text-xs font-medium text-content-2';
/** «al mes» · «a los 3 meses» (evita «a los 1 mes»). */
const enMeses = (n: number) => (n === 1 ? 'al mes' : `a los ${n} meses`);

const MembershipPlanForm: React.FC<Props> = ({ plan, rewards, loyaltyMode, onClose, onSaved }) => {
  const b = (t: BenefitRule['type']) => plan?.benefits.find((r) => r.type === t) as any;
  const [name, setName] = useState(plan?.name || '');
  const [description, setDescription] = useState(plan?.description || '');
  const [price, setPrice] = useState(plan?.price != null ? String(plan.price) : '');
  const [modo, setModo] = useState<'tiempo' | 'productos'>(plan?.quota_total ? 'productos' : 'tiempo');
  const [meses, setMeses] = useState<string>(plan?.duration_months != null && !plan.quota_total ? String(plan.duration_months) : '1');
  const [cupo, setCupo] = useState(plan?.quota_total != null ? String(plan.quota_total) : '');
  // Solo planes por productos: ¿lo que sobre caduca? '' = no caduca (por defecto, Carlos 20-oct); N = a los N meses.
  const [sobra, setSobra] = useState<string>(plan?.quota_total ? String(plan.leftover_months ?? plan.duration_months ?? '') : '');
  const [accrues, setAccrues] = useState(plan ? plan.accrues_points : true);
  const [cortesia, setCortesia] = useState<boolean>(!!b('checkin_reward'));
  const [cortesiaReward, setCortesiaReward] = useState<string>(b('checkin_reward') ? String(b('checkin_reward').reward_id) : '');
  const [cupoPremios, setCupoPremios] = useState<boolean>(!!b('quota_reward'));
  const [cupoIds, setCupoIds] = useState<string[]>(b('quota_reward') ? b('quota_reward').reward_ids.map(String) : []);
  const [extra, setExtra] = useState<boolean>(!!b('points_multiplier') || !!b('cashback_pct'));
  const [factor, setFactor] = useState<string>(b('points_multiplier') ? String(b('points_multiplier').factor) : '2');
  const [pct, setPct] = useState<string>(b('cashback_pct') ? String(b('cashback_pct').pct) : '');
  const [notifyOn, setNotifyOn] = useState(plan ? plan.notify.enabled : true);
  const channel = plan?.notify.channel || 'email';   // (F4) el canal lo decide el editor de mensajes; se conserva el valor guardado
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const activos = useMemo(() => rewards.filter((r) => r.isActive !== false), [rewards]);
  const conCupo = modo === 'productos' && /^\d+$/.test(cupo) && Number(cupo) > 0;
  const valido = name.trim().length >= 2 && (modo === 'tiempo' ? meses !== '' : conCupo) && (!cortesia || !!cortesiaReward) && (!extra || (loyaltyMode === 'cashback' ? Number(pct) > 0 : Number(factor) > 1));

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valido || busy) return;
    setBusy(true); setError('');
    const benefits: BenefitRule[] = [];
    if (cortesia && cortesiaReward) benefits.push({ type: 'checkin_reward', reward_id: Number(cortesiaReward), per_day: 1 });
    if (cupoPremios && conCupo) benefits.push({ type: 'quota_reward', reward_ids: cupoIds.map(Number) });
    if (extra) benefits.push(loyaltyMode === 'cashback' ? { type: 'cashback_pct', pct: Number(pct) } : { type: 'points_multiplier', factor: Number(factor) });
    // Beneficios que este formulario no edita (hoy «Precio de miembro», que se configura desde la caja del POS):
    // se conservan tal cual; antes (24-oct-2026) editar el plan los borraba sin avisar.
    const editables = new Set<BenefitRule['type']>(['checkin_reward', 'quota_reward', 'points_multiplier', 'cashback_pct']);
    for (const r of plan?.benefits || []) if (!editables.has(r.type)) benefits.push(r);
    const body: PlanInput = {
      name: name.trim(), description: description.trim() || null, price: price === '' ? null : Number(price),
      duration_months: modo === 'tiempo' ? Number(meses) : null, quota_total: conCupo ? Number(cupo) : null,
      leftover_months: conCupo && sobra !== '' ? Number(sobra) : null,
      accrues_points: accrues, benefits, notify: { enabled: notifyOn, channel },
    };
    try {
      const saved = plan ? await membershipsApi.updatePlan(plan.id, body) : await membershipsApi.createPlan(body);
      onSaved(saved);
    } catch (err: any) {
      setError(err?.message || 'No se pudo guardar');
      setBusy(false);
    }
  };

  return createPortal(
    <div className="modal-overlay fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={guardar} data-testid="plan-modal"
        className="modal-sheet w-full max-h-[92vh] overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 shadow-2xl sm:max-w-2xl sm:rounded-2xl">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-bold text-content">
            <BadgeCheck size={18} className="text-emerald-600" /> {plan ? 'Editar plan' : 'Nuevo plan de membresía'}
          </h2>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="rounded-full p-1.5 text-content-3 hover:bg-surface-2"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <label className={lbl}>Nombre</label>
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={120} data-testid="plan-nombre" placeholder="Club del Café, 10 comidas…" className={cls} />
          </div>
          <div>
            <label className={lbl}>Precio (informativo)</label>
            <input type="number" min={0} step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="plan-precio" placeholder="0" className={cls} />
          </div>
          <div className="sm:col-span-3">
            <label className={lbl}>Descripción (la ve tu equipo al vender)</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} placeholder="Qué incluye, condiciones…" className={cls} />
          </div>
        </div>

        <h3 className="mb-1 mt-4 text-sm font-semibold text-content">Vigencia <span className="font-normal text-content-3">— ¿cómo vence la membresía?</span></h3>
        {/* Una sola pregunta con dos modos (Carlos 21-oct-2026): antes había meses + productos + «si le sobran», tres sitios para lo mismo. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {([
            ['tiempo', 'Por tiempo', '«Club del Café, 1 mes»: vale ese tiempo desde que se vende.'],
            ['productos', 'Por productos incluidos', '«10 comidas»: el cliente paga 10 productos y los va usando al canjear; al llegar a 0 se agota.'],
          ] as const).map(([m, titulo, ejemplo]) => (
            <div key={m} onClick={() => setModo(m)} data-testid={`plan-modo-${m}`}
              className={`cursor-pointer rounded-lg border p-3 transition-colors ${modo === m ? 'border-accent bg-accent/5' : 'border-line hover:bg-surface-2'}`}>
              <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-content">
                <input type="radio" name="plan-modo" checked={modo === m} onChange={() => setModo(m)} /> {titulo}
              </label>
              <p className="mt-1 text-xs text-content-3">{ejemplo}</p>
              {m === 'tiempo' && modo === 'tiempo' && (
                <select value={meses} onChange={(e) => setMeses(e.target.value)} data-testid="plan-meses" className={`${cls} mt-2`} onClick={(e) => e.stopPropagation()}>
                  {MESES.map((n) => <option key={n} value={n}>{n} {n === 1 ? 'mes' : 'meses'}</option>)}
                </select>
              )}
              {m === 'productos' && modo === 'productos' && (
                <input type="number" min={1} max={9999} value={cupo} onChange={(e) => setCupo(e.target.value)} data-testid="plan-cupo" placeholder="¿Cuántos? p. ej. 10" className={`${cls} mt-2`} onClick={(e) => e.stopPropagation()} />
              )}
            </div>
          ))}
        </div>
        {conCupo && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-content-2">
            <span>Si le sobran productos:</span>
            <select value={sobra} onChange={(e) => setSobra(e.target.value)} data-testid="plan-sobra" className={`${cls} w-auto`}>
              <option value="">No caducan (recomendado)</option>
              {[1, 2, 3, 6, 12, 18, 24, 36].map((n) => <option key={n} value={n}>Caducan {enMeses(n)} de la venta</option>)}
            </select>
          </div>
        )}
        {modo === 'productos' && !conCupo && <p className="mt-1 text-xs text-red-600 dark:text-red-400">Escribe cuántos productos incluye.</p>}
        {(modo === 'tiempo' || conCupo) && (
          <p className="mt-1 text-xs text-content-2" data-testid="plan-resumen">
            <b>Así vence:</b>{' '}
            {modo === 'tiempo' && <>{enMeses(Number(meses))} de venderla.</>}
            {conCupo && (sobra === ''
              ? <>cuando el cliente use sus {cupo} productos, sin importar cuánto tarde.</>
              : <>cuando el cliente use sus {cupo} productos o {enMeses(Number(sobra))} de venderla, lo que pase primero.</>)}
            {' '}Renovar la misma membresía la extiende{conCupo ? ' y suma los productos' : ''}.
          </p>
        )}
        {conCupo && !cupoPremios && <p className="mt-1 text-xs text-amber-700 dark:text-amber-300" data-testid="plan-cupo-aviso">Pusiste productos incluidos pero nada los resta: marca abajo «Qué resta de los productos incluidos» o nunca bajarán.</p>}

        <h3 className="mb-1 mt-4 text-sm font-semibold text-content">Beneficios</h3>
        <div className="space-y-2 text-sm text-content-2">
          <label className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" checked={accrues} onChange={(e) => setAccrues(e.target.checked)} data-testid="plan-acumula" />
            <span>Los miembros también acumulan {loyaltyMode === 'cashback' ? 'cashback' : 'puntos'} con cada visita <span className="text-content-3">(apágalo si «ya nada más tienen su membresía»)</span></span>
          </label>
          <label className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" checked={cortesia} onChange={(e) => setCortesia(e.target.checked)} data-testid="plan-cortesia" />
            <span className="flex-1">
              Cortesía por visita <span className="text-content-3">(una al día, la entrega tu caja)</span>
              {cortesia && (
                <select value={cortesiaReward} onChange={(e) => setCortesiaReward(e.target.value)} data-testid="plan-cortesia-premio" className={`${cls} mt-1`}>
                  <option value="">Elige la recompensa…</option>
                  {activos.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
                </select>
              )}
            </span>
          </label>
          <label className={`flex items-start gap-2 ${conCupo ? '' : 'opacity-50'}`}>
            <input type="checkbox" className="mt-1" disabled={!conCupo} checked={cupoPremios && conCupo} onChange={(e) => setCupoPremios(e.target.checked)} data-testid="plan-cupo-premios" />
            <span className="flex-1">
              Qué resta de los productos incluidos: cada canje de estas recompensas resta 1 <span className="text-content-3">(sin tocar sus puntos{conCupo ? '' : ' · necesita productos incluidos arriba'})</span>
              {cupoPremios && conCupo && (
                <span className="mt-1 block rounded-lg border border-line p-2">
                  <span className="block text-xs text-content-3 mb-1">Cuáles cuentan (ninguna marcada = cualquiera). Ej.: 10 productos incluidos + recompensa «Comida del día» = 10 comidas que el cliente canjea en el kiosco o en caja.</span>
                  {activos.length === 0 && <span className="text-xs text-content-3">Aún no tienes recompensas.</span>}
                  {activos.map((r) => (
                    <label key={r.id} className="mr-3 inline-flex items-center gap-1 text-xs">
                      <input type="checkbox" checked={cupoIds.includes(r.id)} onChange={(e) => setCupoIds(e.target.checked ? [...cupoIds, r.id] : cupoIds.filter((x) => x !== r.id))} /> {r.title}
                    </label>
                  ))}
                </span>
              )}
            </span>
          </label>
          <label className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" checked={extra} onChange={(e) => setExtra(e.target.checked)} data-testid="plan-extra" />
            <span className="flex-1">
              {loyaltyMode === 'cashback' ? 'Cashback especial para miembros' : 'Puntos extra por visita'}
              {extra && (loyaltyMode === 'cashback' ? (
                <span className="mt-1 flex items-center gap-2"><input type="number" min={0.1} max={100} step="0.1" value={pct} onChange={(e) => setPct(e.target.value)} className={`${cls} w-28`} /> % (gana el mayor frente al general)</span>
              ) : (
                <span className="mt-1 flex items-center gap-2">× <input type="number" min={1.1} max={10} step="0.1" value={factor} onChange={(e) => setFactor(e.target.value)} className={`${cls} w-28`} /> veces los puntos de cada visita</span>
              ))}
            </span>
          </label>
        </div>

        <h3 className="mb-1 mt-4 text-sm font-semibold text-content">Aviso de vencimiento</h3>
        <div className="flex flex-wrap items-center gap-3 text-sm text-content-2">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={notifyOn} onChange={(e) => setNotifyOn(e.target.checked)} data-testid="plan-aviso" /> Avisar al cliente antes de que venza
          </label>
        </div>
        <p className="mt-1 text-[11px] text-content-3">Se avisa una vez, unos días antes (o cuando queden pocos productos). El texto y el canal (correo por defecto; WhatsApp como respaldo si el cliente no tiene correo) se editan en la pestaña <a href="/dashboard/memberships?tab=avisos" className="underline">Avisos</a> de Membresías.</p>

        {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-line px-4 py-2 text-sm text-content-2 hover:bg-surface-2">Cancelar</button>
          <button type="submit" disabled={!valido || busy} data-testid="plan-guardar"
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
            {busy && <Loader2 size={14} className="animate-spin" />} {plan ? 'Guardar' : 'Crear plan'}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
};

export default MembershipPlanForm;
